import { Box } from '@mui/joy';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { Suspense, useEffect, useRef } from 'react';

import { SystemNotificationModal } from '@/components/modals/SystemNotificationModal';
import { BackendHeader } from '@/components/navigations/BackendHeader';
import { Footer } from '@/components/navigations/Footer';
import { PendingComponent } from '@/components/router/PendingComponent';
import { makeMetaTitle, META_ROBOTS_NOINDEX } from '@/modules/META';
import { UserGuard } from '@/modules/UserGuard';
import { AdminLevelUserStatus, UserRole, UserStatus } from '@/modules/api';
import {
  backendApiAuthenticationGetUserOptions,
  backendApiCaslGetRulesOptions,
} from '@/modules/api/@tanstack/react-query.gen';
import { useSystemNotificationQuery } from '@/queries/useSystemNotificationQuery';
import { useUserStore } from '@/stores/useUserStore';

export const Route = createFileRoute('/verwaltung')({
  beforeLoad: async ({ context: { queryClient }, location }) => {
    try {
      const user = await queryClient.ensureQueryData(backendApiAuthenticationGetUserOptions());

      if (!UserGuard.isUser(user) || user.status !== UserStatus.ACTIVE) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/registrierung', replace: true });
      }

      if (location.pathname === '/verwaltung' && UserGuard.isUser(user)) {
        if (user.role !== UserRole.ADMIN) {
          const adminLevelId = user.adminLevels?.find(
            ({ status }) => status === AdminLevelUserStatus.ACTIVE,
          )?.adminLevelId;

          if (adminLevelId) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw redirect({
              to: '/verwaltung/{-$adminLevelId}/meldungen',
              params: {
                adminLevelId,
              },
              replace: true,
            });
          }

          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw redirect({
            throw: true,
            to: '/verwaltung/profil',
            replace: true,
          });
        }
      }

      const { rules } = await queryClient.ensureQueryData(backendApiCaslGetRulesOptions());

      return { user, rules: rules as (string | number | Record<string, object>)[][] };
    } catch (error) {
      if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 401) {
        redirect({
          throw: true,
          reloadDocument: true,
          href: `/api/authentication/login?relayState=${encodeURIComponent(location.pathname)}`,
          replace: true,
        });
      }

      throw error;
    }
  },
  head: () => ({
    meta: [makeMetaTitle(['Verwaltung']), META_ROBOTS_NOINDEX],
  }),
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,

  pendingComponent: PendingComponent,
});

function RouteComponent() {
  const notificationDialogOpen = useUserStore((state) => state.notificationDialogOpen);
  const notificationsRead = useUserStore((state) => state.notificationsRead);
  const { setNotificationDialogOpen } = useUserStore((state) => state.actions);
  const { data: systemNotifications } = useSystemNotificationQuery('VALID');
  const hasCheckedAutoOpenRef = useRef(false);

  useEffect(() => {
    if (hasCheckedAutoOpenRef.current || !systemNotifications) {
      return;
    }

    hasCheckedAutoOpenRef.current = true;

    if (systemNotifications.some(({ id }) => !notificationsRead.includes(id))) {
      setNotificationDialogOpen(true);
    }
  }, [notificationsRead, setNotificationDialogOpen, systemNotifications]);

  return (
    <Box sx={{ backgroundColor: 'neutral.200', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BackendHeader />
      {notificationDialogOpen ? (
        <Suspense>
          <SystemNotificationModal onClose={() => setNotificationDialogOpen(false)} />
        </Suspense>
      ) : null}
      <Outlet />
      <Footer />
    </Box>
  );
}
